import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Box, Divider, Page, Tabs, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  EnterpriseListingEvent,
  EnterprisePageFeature,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyBoostAdsWebApiHandler_getBoostListingDetails,
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get,
  useRentalManagerUserSelfServiceOnboardingWebApiHandler_submitManualListing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { calculateRequiredFieldsErrors } from '../../components/MFAccordion/utils';
import { AccordionStateProvider } from '../../contexts/AccordionStateContext';
import { useGetListingDetails, useGetListingRecommendations, useGetTours } from '../../hooks/queries/properties';
import { useActivateListing } from '../../hooks/queries/useActivateListing';
import { useSyndicationUpdates } from '../../hooks/useSyndicationUpdates';

import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';
import { LISTING_TABS_KEYS, LISTING_TABS_OPTIONS } from './components/MFSubHeader/components/ListingNavTabs/constants';

import { useAuth } from '../../components/AuthProvider';
import ListingActivationTerms from '../../components/ListingActivationTerms';
import ListingCompletenessScore from '../../components/ListingCompletenessScore';
import Banners from '../../components/ListingDetails/Banners';
import DuplicateListingDetails from '../../components/ListingDetails/DuplicateListingDetails';
import Footer from '../../components/ListingDetails/Footer';
import ListingHeader from '../../components/ListingDetails/ListingHeader';
import ListingStats from '../../components/ListingDetails/ListingStats';
import { MFAccordion } from '../../components/MFAccordion';
import ActivateListingErrorModal from '../../components/modals/ActivateListingErrorModal';
import PreActivationModal from '../../components/modals/PreActivationModal';
import TransferFeedListingModal from '../../components/modals/TransferFeedListingModal';
import StickyContainer from '../../components/StickyContainer';

import { EVENT_IDS } from '../../analytics/events/reviewPage';
import MetricsSummaryPage from '../MetricsSummaryPage';
import AccordionStateHeader from './AccordionStateHeader';
import PropertyCard from './PropertyCard';

import type { FreemiumDatarouterError } from '../../components/modals/FreemiumErrorModal';

const PropertyDetailsContainer = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();
  const { user } = useAuth();

  const listingHeaderRef = useRef<HTMLDivElement | null>(null);

  const [activateListingError, setActivateListingError] = useState<FreemiumDatarouterError | null>(null);
  const [displayPreActivationModal, setDisplayPreActivationModal] = useState(false);
  const [isListingHeaderVisible, setIsListingHeaderVisible] = useState(true);

  const { listingAlias } = router.query as { listingAlias: string };

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  useSyndicationUpdates({ listingAlias });

  const { recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: listingDetails?.isMultifamily,
  });

  const { data: costsAndFeesData } = useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get(
    { listingAlias },
    { enabled: Boolean(listingAlias && listingDetails?.isMultifamily), meta: { tags: ['mf-costsAndFees'] } },
  );

  const { data: boostListingData } = useRentalManagerUserPropertyBoostAdsWebApiHandler_getBoostListingDetails(
    { customerId: listingDetails?.customerId ?? '', listingAlias },
    {
      enabled: Boolean(listingDetails?.customerId && listingAlias),
      meta: {
        tags: ['boostListingDetails', listingAlias],
        context: {
          api: 'getBoostListingDetails',
          details: { customerId: listingDetails?.customerId ?? '', listingAlias, userEmail: user?.email },
        },
      },
    },
  );

  const { vrModels, externalTours } = useGetTours(listingAlias, listingDetails);

  const requiredFieldsErrors = useMemo(
    () =>
      calculateRequiredFieldsErrors(
        listingDetails
          ? {
              ...listingDetails,
              vrModels: vrModels.map((vrModel) => ({ ...vrModel, creatorZuid: String(vrModel.creatorZuid) })),
              externalTours,
            }
          : undefined,
        recommendationsBySection,
        costsAndFeesData,
        boostListingData,
      ),
    [listingDetails, recommendationsBySection, costsAndFeesData, vrModels, externalTours],
  );

  const { mutate: submitListing } = useRentalManagerUserSelfServiceOnboardingWebApiHandler_submitManualListing();

  const { mutate: activateListing, isPending: isActivateListingPending } = useActivateListing();

  // Determine selected tab from URL pathname
  const selectedListingNav = router.pathname.includes('/metrics')
    ? LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.METRICS].id
    : LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.LISTING].id;

  useEffect(() => {
    if (user && listingDetails && user.customers.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
      RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit({
        page: EnterprisePageFeature.PROPERTY_EDIT,
      }).catch(() => {});

      RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent({
        feedId: listingDetails.feedId,
        feedListingId: listingDetails.feedListingId,
        event: EnterpriseListingEvent.VIEW_PROPERTY,
      }).catch(() => {});
    }
  }, [listingAlias, listingDetails, user]);

  useEffect(() => {
    if (!listingHeaderRef.current) return;
    const observer = new window.IntersectionObserver(([entry]) => setIsListingHeaderVisible(entry.isIntersecting), {
      threshold: 0.1,
      // to account for the height of the sticky header
      rootMargin: '-152px 0px 0px 0px',
    });
    observer.observe(listingHeaderRef.current);
    return () => observer.disconnect();
  }, [listingHeaderRef.current]);

  const activateAndSyncListing = () => {
    activateListing(
      { getParams: { listingAlias } },
      {
        onSuccess: () => {
          window.scrollTo(0, 0);
          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
          });
        },
        onError: (error) => {
          setActivateListingError(error as FreemiumDatarouterError);

          logError({
            error,
            errorType: 'HpppActivationError',
            context: 'PropertyDetailsContainer.activateAndSyncListing',
          });
        },
      },
    );
  };

  const handlePublishClick = () => {
    if (listingDetails?.isFeed) {
      setDisplayPreActivationModal(true);

      return;
    }

    if (
      listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress &&
      !listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted
    ) {
      submitListing(
        { getParams: { listingAlias } },
        {
          onSuccess: () => {
            window.location.href = `${EXTERNAL_ROUTES.SETUP_TASKS}?alias=${listingAlias}`;
          },
          onError: (err) => {
            enqueueToast(<Toast tone="critical">{DatarouterError.getDisplayMessage(err)}</Toast>);
          },
        },
      );
      return;
    }

    // activate listing, then call handleChargeAndActivateListingResponse onSuccess
    activateAndSyncListing();
  };

  const renderPageContent = () => {
    const canPublish = Object.keys(requiredFieldsErrors).every(
      (key) => !requiredFieldsErrors[key as keyof typeof requiredFieldsErrors]?.isIncomplete,
    );

    if (selectedListingNav === LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.LISTING].id) {
      return (
        <>
          <Banners />
          {showDupeDetails && !canFeedConnectSwitchSource && <DuplicateListingDetails />}
          {user?.isEnterpriseCustomer && <ListingStats />}
          <Box css={{ marginBottom: '600' }}>
            <ListingCompletenessScore
              requiredFieldsErrors={Object.values(requiredFieldsErrors)
                .filter((value) => value?.isIncomplete)
                .map((value) => value.isIncomplete)}
            />
          </Box>
          {listingDetails?.isFeed && (
            <ListingHeader ref={listingHeaderRef} title={`Source: ${listingDetails?.feedName}`} />
          )}
          {!listingDetails?.isFeed && canFeedConnectSwitchSource && <ListingHeader ref={listingHeaderRef} />}

          <MFAccordion requiredFieldsErrors={requiredFieldsErrors} />

          <ListingActivationTerms
            isTextCenter={true}
            actionText={`By ${listingDetails?.active ? 'deactivating' : 'publishing'} this listing`}
          />
          <Footer
            onPublishClick={handlePublishClick}
            isPublishedDisabled={!canPublish}
            isPublishLoading={isActivateListingPending}
          />
        </>
      );
    }

    if (selectedListingNav === LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.METRICS].id) {
      return <MetricsSummaryPage />;
    }
  };

  const showDupeDetails = !!listingDetails?.topDupesDetails?.dupes?.length;
  const canFeedConnectSwitchSource = user?.features?.canSwitchListingSource ?? false;

  const getBreadCrumbTitle = () => {
    if (listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress) {
      return 'Listing Type Selection';
    }
    return 'Your properties';
  };

  const getBreadCrumbUrl = () => {
    if (listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress) {
      return `${EXTERNAL_ROUTES.SETUP_TASKS}/${listingAlias}/listingType`;
    }
    return `/rental-manager${ROUTES.PROPERTIES}`;
  };

  return (
    <Page.Root
      data-testid="PropertyDetailsContainer-Page"
      css={{ backgroundColor: 'bg.neutral' }}
      data-eventid={listingDetails?.active ? EVENT_IDS.ACTIVE_LISTING_VIEW : undefined}
    >
      <Page.Breadcrumb css={{ paddingInline: '0', marginInline: '0' }}>
        <Box css={{ paddingInline: '400' }}>
          <TextButton asChild icon={<IconChevronLeftFilled />}>
            <a href={getBreadCrumbUrl()}>{getBreadCrumbTitle()}</a>
          </TextButton>
        </Box>
      </Page.Breadcrumb>

      <Page.Content
        css={{
          backgroundColor: 'bg.neutral',
          padding: '0 !important',
          minHeight: '65vh',
          marginBottom: '128px',
        }}
      >
        <Box
          css={{
            padding: '{spacing.400}',
            paddingBottom: '0',
            md: {
              border: '1px solid {colors.border.subtle}',
              borderRadius: '100',
            },
          }}
        >
          <PropertyCard />

          <Box css={{ paddingBottom: '600', marginTop: '400' }}>
            {listingDetails?.lastActivatedMs ? (
              <Tabs.Root selected={selectedListingNav}>
                <Tabs.List aria-label="MF-Listing-Tab-Options">
                  {[LISTING_TABS_KEYS.LISTING, LISTING_TABS_KEYS.METRICS].map((key) => {
                    const tabOption = LISTING_TABS_OPTIONS[key];
                    const href =
                      tabOption.id === 'metrics'
                        ? { pathname: ROUTES.METRICS, query: { listingAlias } }
                        : { pathname: ROUTES.LISTING, query: { listingAlias } };

                    return (
                      <Tabs.Tab key={tabOption.id} value={tabOption.id} asChild>
                        <Link href={href}>{tabOption.label}</Link>
                      </Tabs.Tab>
                    );
                  })}
                </Tabs.List>
              </Tabs.Root>
            ) : (
              <Divider />
            )}
          </Box>

          <AccordionStateProvider>
            {selectedListingNav === LISTING_TABS_OPTIONS[LISTING_TABS_KEYS.LISTING].id && (
              <StickyContainer passDownFixedStatus={true} addBorderBottom={true}>
                <AccordionStateHeader isListingHeaderVisible={isListingHeaderVisible} />
              </StickyContainer>
            )}
            {renderPageContent()}
          </AccordionStateProvider>
        </Box>
      </Page.Content>

      <PreActivationModal
        displayPreActivationModal={displayPreActivationModal}
        setDisplayPreActivationModal={setDisplayPreActivationModal}
        onSubmit={activateAndSyncListing}
      />

      <TransferFeedListingModal listingAlias={listingAlias} isMultifamily={listingDetails?.isMultifamily} />

      {activateListingError && (
        <ActivateListingErrorModal onClose={() => setActivateListingError(null)} error={activateListingError} />
      )}
    </Page.Root>
  );
};

export default PropertyDetailsContainer;
